import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard, Users, UserPlus, CalendarOff, Clock, CalendarRange,
  Timer, Wallet, Receipt, Palmtree, FileMinus2, Landmark, FileClock,
  HeartPulse, HardHat, Siren, Brain, Gift, FileSpreadsheet, Target,
  MessagesSquare, GraduationCap, BarChart3, Settings, UsersRound,
  ClipboardCheck, Laptop, ShieldCheck,
} from "lucide-react";

/**
 * Menu agrupado por JORNADA, não por tabela do banco.
 *
 * Cada item tem ÍCONE. Ponto genérico no lugar de ícone é o que dá cara de
 * template: o olho não consegue usar a forma para achar o item, e a lista
 * inteira vira parede de texto. Ícone é o que permite bater o olho e encontrar.
 *
 * Rótulos são CHAVE de i18n desde a primeira string: o CRM cravou PT-BR em
 * string e hoje está preso.
 */
export type ItemMenu = {
  tipo: "item";
  chave: string;
  para: string;
  icone: LucideIcon;
  /** Permissão exigida. Sem ela o item não aparece. */
  permissao?: string;
  /**
   * Chave de alerta que vira contador ao lado do item.
   *
   * São as chaves que `painel.resumo` devolve, e só existem chaves de tabela
   * que existe: ponto e folha ainda não têm tabela, então esses itens não têm
   * contador em vez de terem um zero que mente.
   */
  contador?: readonly string[];
  /** Contador com prazo legal atrás fica em crítico, não em cinza. */
  contadorSevero?: boolean;
};
export type CabecalhoMenu = { tipo: "cabecalho"; chave: string; icone?: LucideIcon };
export type EntradaMenu = ItemMenu | CabecalhoMenu;

export const MENU: readonly EntradaMenu[] = [
  { tipo: "item", chave: "menu.painel", para: "/", icone: LayoutDashboard },

  { tipo: "cabecalho", chave: "menu.grupo.pessoas" },
  { tipo: "item", chave: "menu.pessoas", para: "/pessoas", icone: Users, permissao: "pessoa.ler" },
  { tipo: "item", chave: "menu.admissao", para: "/admissao", icone: UserPlus, permissao: "vinculo.escrever" },
  { tipo: "item", chave: "menu.afastamentos", para: "/afastamentos", icone: CalendarOff, permissao: "vinculo.ler" },

  { tipo: "cabecalho", chave: "menu.grupo.jornada" },
  { tipo: "item", chave: "menu.ponto", para: "/ponto", icone: Clock, permissao: "ponto.ler" },
  { tipo: "item", chave: "menu.espelho", para: "/ponto/espelho", icone: CalendarRange, permissao: "ponto.ler" },
  { tipo: "item", chave: "menu.bancoDeHoras", para: "/ponto/banco-de-horas", icone: Timer, permissao: "ponto.ler" },

  { tipo: "cabecalho", chave: "menu.grupo.atividade" },
  { tipo: "item", chave: "menu.combinado", para: "/atividade",
    icone: ClipboardCheck, permissao: "atividade.ler" },
  { tipo: "item", chave: "menu.equipamentos", para: "/equipamentos",
    icone: Laptop, permissao: "equipamento.ler" },
  { tipo: "item", chave: "menu.politicaMonitoramento", para: "/atividade/politica",
    icone: ShieldCheck, permissao: "atividade.ler" },

  { tipo: "cabecalho", chave: "menu.grupo.folha" },
  { tipo: "item", chave: "menu.folha", para: "/folha", icone: Wallet, permissao: "folha.ler" },
  { tipo: "item", chave: "menu.rubricas", para: "/folha/rubricas", icone: Receipt, permissao: "folha.ler" },
  { tipo: "item", chave: "menu.ferias", para: "/folha/ferias", icone: Palmtree, permissao: "ferias.ler",
    contador: ["ferias.dobra"], contadorSevero: true },
  { tipo: "item", chave: "menu.rescisao", para: "/folha/rescisao", icone: FileMinus2, permissao: "vinculo.desligar" },

  { tipo: "cabecalho", chave: "menu.grupo.obrigacoes" },
  { tipo: "item", chave: "menu.esocial", para: "/esocial", icone: Landmark, permissao: "esocial.ler",
    contador: ["esocial.rejeitado", "esocial.prazo"], contadorSevero: true },
  { tipo: "item", chave: "menu.arquivosFiscais", para: "/esocial/arquivos", icone: FileClock, permissao: "esocial.ler" },

  { tipo: "cabecalho", chave: "menu.grupo.beneficios" },
  { tipo: "item", chave: "menu.beneficios", para: "/beneficios", icone: Gift, permissao: "beneficio.ler" },
  { tipo: "item", chave: "menu.faturas", para: "/beneficios/faturas", icone: FileSpreadsheet,
    permissao: "beneficio.ler", contador: ["beneficio.divergencia"] },

  { tipo: "cabecalho", chave: "menu.grupo.sst" },
  { tipo: "item", chave: "menu.exames", para: "/sst/exames", icone: HeartPulse, permissao: "sst.ler",
    contador: ["aso.vencido"], contadorSevero: true },
  { tipo: "item", chave: "menu.epi", para: "/sst/epi", icone: HardHat, permissao: "sst.ler" },
  { tipo: "item", chave: "menu.acidentes", para: "/sst/acidentes", icone: Siren, permissao: "sst.ler" },
  { tipo: "item", chave: "menu.psicossocial", para: "/sst/psicossocial", icone: Brain, permissao: "sst.ler" },

  { tipo: "cabecalho", chave: "menu.grupo.desenvolvimento" },
  { tipo: "item", chave: "menu.desempenho", para: "/desempenho", icone: Target, permissao: "desempenho.ler" },
  { tipo: "item", chave: "menu.clima", para: "/clima", icone: MessagesSquare, permissao: "clima.ler" },
  { tipo: "item", chave: "menu.treinamento", para: "/treinamento", icone: GraduationCap, permissao: "pessoa.ler" },

  { tipo: "cabecalho", chave: "menu.grupo.gestao" },
  { tipo: "item", chave: "menu.relatorios", para: "/relatorios", icone: BarChart3, permissao: "relatorio.ler" },
  { tipo: "item", chave: "menu.configuracoes", para: "/configuracoes", icone: Settings, permissao: "config.ler" },
  { tipo: "item", chave: "menu.equipe", para: "/configuracoes/equipe", icone: UsersRound, permissao: "config.ler" },
];

/**
 * O menu é filtrado pela PERMISSÃO que o servidor devolveu.
 *
 * Esconder o que a pessoa não pode fazer não é segurança (a procedure recusa
 * de qualquer jeito), é respeito: menu com 26 itens dos quais 20 dão erro de
 * permissão é menu que ensina o usuário a não confiar no menu.
 */
export function menuVisivel(
  permissoes: readonly string[], menu: readonly EntradaMenu[] = MENU,
): EntradaMenu[] {
  const podeVer = (e: EntradaMenu) =>
    e.tipo === "cabecalho" || !e.permissao || permissoes.includes(e.permissao);
  const filtrado = menu.filter(podeVer);

  // Cabeçalho que ficou sem item embaixo sai junto: título de seção vazia é
  // ruído que promete algo que não existe.
  return filtrado.filter((e, i) => {
    if (e.tipo !== "cabecalho") return true;
    const proximo = filtrado[i + 1];
    return proximo !== undefined && proximo.tipo === "item";
  });
}
